import {
  BanknotesIcon,
  DocumentTextIcon,
  EllipsisVerticalIcon,
  EnvelopeIcon,
  PaperAirplaneIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { useMemo } from 'react';
import { toast } from 'sonner';

import { DataTable, TableColumn } from '@/components/common/DataTable';
import {
  CarrierTrackingBadge,
  MailingClassBadge,
  StatusBadge,
} from '@/components/postgrid/common';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { usePagination } from '@/hooks/usePagination';
import { cn } from '@/lib/utils';
import type {
  ChequeMailableItem,
  LetterMailableItem,
  PostGridObjectStatus,
} from '@/services/api/generated/postgridService';
import {
  usePostgridChequesCancelMutation,
  usePostgridChequesProgressTestMutation,
  usePostgridLettersCancelMutation,
  usePostgridLettersProgressTestMutation,
  usePostgridMailablesListQuery,
} from '@/services/api/postgridService';

export interface MailableTableProps {
  matterId?: string;
  onDetailsClick?: (id: string, type: 'cheque' | 'letter') => void;
}

// Discriminated union type for mailable items
type MailableItem =
  | ({ mailable_type: 'cheque' } & ChequeMailableItem)
  | ({ mailable_type: 'letter' } & LetterMailableItem);

// Type guard for cheque
const isCheque = (
  item: MailableItem,
): item is { mailable_type: 'cheque' } & ChequeMailableItem => {
  return item.mailable_type === 'cheque';
};

// Filter options - core PostGrid workflow statuses only
const STATUS_OPTIONS = [
  { label: 'Ready', value: 'ready' },
  { label: 'Creating', value: 'creating' },
  { label: 'Printing', value: 'printing' },
  { label: 'Processed for Delivery', value: 'processed_for_delivery' },
  { label: 'Sent to Carrier', value: 'completed' },
];

// Format currency
const formatCurrency = (amount: string | number) => {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(num);
};

// Format date in traditional MM/DD/YYYY format
const formatDate = (dateStr: string | null | undefined) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
};

// Row actions component
const RowActions = ({
  item,
  onDetailsClick,
}: {
  item: MailableItem;
  onDetailsClick?: (id: string, type: 'cheque' | 'letter') => void;
}) => {
  const [cancelCheque] = usePostgridChequesCancelMutation();
  const [progressTestCheque] = usePostgridChequesProgressTestMutation();
  const [cancelLetter] = usePostgridLettersCancelMutation();
  const [progressTestLetter] = usePostgridLettersProgressTestMutation();

  const isTest = !item.live;
  const type = isCheque(item) ? 'cheque' : 'letter';
  const canCancel = item.status === 'creating' || item.status === 'ready';
  const showCancelOption =
    item.status !== 'cancelled' && item.status !== 'delivered' && item.status !== 'failed';

  const getCancelDisabledReason = () => {
    const statusLabels: Record<string, string> = {
      printing: 'printing',
      processed_for_delivery: 'processed for delivery',
      completed: 'sent to carrier',
      in_transit: 'in transit',
      in_local_area: 'in local area',
      out_for_delivery: 'out for delivery',
      re_routed: 're-routed',
      returned_to_sender: 'returned to sender',
      processed: 'processed',
    };
    const label = statusLabels[item.status || ''] || item.status || 'unknown';
    return `Cannot cancel — ${type} is already ${label}`;
  };

  const handleCancel = async () => {
    try {
      if (isCheque(item)) {
        await cancelCheque({ id: item.id }).unwrap();
      } else {
        await cancelLetter({ id: item.id }).unwrap();
      }
      toast.success(`${type === 'cheque' ? 'Cheque' : 'Letter'} cancelled`);
    } catch (error) {
      const errorMessage =
        (error as { data?: { error?: string } })?.data?.error ||
        `Failed to cancel ${type}`;
      toast.error(errorMessage);
    }
  };

  const handleProgressTest = async () => {
    try {
      if (isCheque(item)) {
        await progressTestCheque({ id: item.id }).unwrap();
      } else {
        await progressTestLetter({ id: item.id }).unwrap();
      }
      toast.success('Test status progressed');
    } catch {
      toast.error('Failed to progress test');
    }
  };

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className="flex items-center gap-1 justify-end"
      onClick={(e) => e.stopPropagation()}
    >
      {item.final_pdf_url && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 text-zinc-500 hover:text-zinc-900"
          onClick={() => {
            if (item.final_pdf_url)
              window.open(item.final_pdf_url, '_blank', 'noopener,noreferrer');
          }}
        >
          <DocumentTextIcon className="h-4 w-4" />
        </Button>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-zinc-500 hover:text-zinc-900"
          >
            <EllipsisVerticalIcon className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-40">
          <DropdownMenuItem onClick={() => onDetailsClick?.(item.id, type)}>
            View details
          </DropdownMenuItem>

          {isTest && (
            <DropdownMenuItem onClick={handleProgressTest}>
              Progress test
            </DropdownMenuItem>
          )}

          {showCancelOption && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <DropdownMenuItem
                    onClick={canCancel ? handleCancel : undefined}
                    disabled={!canCancel}
                    className={cn(
                      canCancel
                        ? 'text-red-600 focus:text-red-600'
                        : 'text-zinc-400 cursor-not-allowed',
                    )}
                  >
                    Cancel
                  </DropdownMenuItem>
                </span>
              </TooltipTrigger>
              {!canCancel && (
                <TooltipContent side="left">
                  {getCancelDisabledReason()}
                </TooltipContent>
              )}
            </Tooltip>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

// Column definitions - follow TaskListView patterns with fixed widths and resizable
const createColumns = (
  onDetailsClick?: (id: string, type: 'cheque' | 'letter') => void,
): TableColumn<MailableItem>[] => [
  {
    id: 'item',
    header: 'Recipient',
    width: '220px',
    resizable: true,
    hideable: false,
    cell: (row) => {
      const Icon = isCheque(row) ? BanknotesIcon : EnvelopeIcon;
      const typeColor = isCheque(row)
        ? 'text-emerald-600 bg-emerald-50'
        : 'text-indigo-600 bg-indigo-50';

      return (
        <div className="flex items-center gap-2 min-w-0">
          <div className={`shrink-0 p-1 rounded ${typeColor}`}>
            <Icon className="h-3.5 w-3.5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="font-medium text-zinc-900 dark:text-white truncate">
                {row.to_contact_name || 'Unknown'}
              </span>
              {!row.live && (
                <span className="shrink-0 text-[9px] font-medium text-zinc-400 uppercase">
                  Test
                </span>
              )}
            </div>
          </div>
        </div>
      );
    },
  },
  {
    id: 'description',
    header: 'Description',
    width: '180px',
    resizable: true,
    hideable: true,
    cell: (row) => (
      <span className="text-sm text-zinc-600 dark:text-zinc-300 truncate">
        {isCheque(row)
          ? `${formatCurrency(row.amount)}${row.memo ? ` · ${row.memo}` : ''}`
          : row.subject || 'Letter'}
      </span>
    ),
  },
  {
    id: 'mailing_class',
    header: 'Class',
    width: '100px',
    hideable: true,
    cell: (row) => (
      <MailingClassBadge mailingClass={row.mailing_class} size="small" />
    ),
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status',
    width: '130px',
    hideable: false,
    cell: (row) => <StatusBadge status={row.status as PostGridObjectStatus} />,
    filter: {
      type: 'select',
      options: STATUS_OPTIONS,
    },
  },
  {
    id: 'carrier',
    header: 'Tracking',
    width: '140px',
    hideable: true,
    resizable: true,
    cell: (row) =>
      row.carrier_tracking ? (
        <CarrierTrackingBadge carrierTracking={row.carrier_tracking} />
      ) : (
        <span className="text-sm text-zinc-400">—</span>
      ),
  },
  {
    id: 'created_at',
    header: 'Created',
    accessorKey: 'created_at',
    width: '100px',
    hideable: true,
    sortable: true,
    sort: {
      sortKey: 'created_at',
      labels: { asc: 'Oldest first', desc: 'Newest first' },
    },
    cell: (row) => {
      const date = formatDate(row.created_at);
      return date ? (
        <span className="text-sm text-zinc-600 dark:text-zinc-300">{date}</span>
      ) : (
        <span className="text-sm text-zinc-400">—</span>
      );
    },
  },
  {
    id: 'date_sent',
    header: 'Sent',
    accessorKey: 'date_sent',
    width: '100px',
    hideable: true,
    sortable: true,
    sort: {
      sortKey: 'date_sent',
      labels: { asc: 'Oldest first', desc: 'Newest first' },
    },
    cell: (row) => {
      const date = formatDate(row.date_sent);
      return date ? (
        <span className="text-sm text-zinc-600 dark:text-zinc-300">{date}</span>
      ) : (
        <span className="text-sm text-zinc-400">—</span>
      );
    },
  },
  {
    id: 'actions',
    header: '',
    width: '60px',
    align: 'right',
    hideable: false,
    cell: (row) => <RowActions item={row} onDetailsClick={onDetailsClick} />,
  },
];

// Empty state configuration
const getEmptyStateConfig = (matterId?: string, hasFilters?: boolean) => {
  if (hasFilters) {
    return {
      icon: (
        <div className="p-3 rounded-full bg-zinc-100">
          <DocumentTextIcon className="h-6 w-6 text-zinc-400" />
        </div>
      ),
      title: 'No results found',
      description:
        "Try adjusting your search or filters to find what you're looking for.",
    };
  }

  return {
    icon: (
      <div className="p-4 rounded-full bg-gradient-to-br from-indigo-50 to-emerald-50">
        <PaperAirplaneIcon className="h-8 w-8 text-indigo-500" />
      </div>
    ),
    title: 'No mail sent yet',
    description: matterId
      ? 'Cheques and letters for this matter will appear here once created.'
      : 'Your sent cheques and letters will appear here.',
  };
};

export const MailableTable: React.FC<MailableTableProps> = ({
  matterId,
  onDetailsClick,
}) => {
  const { state, actions, searchInput, setSearchInput } = usePagination({
    defaultLimit: 50,
    defaultSort: '-created_at',
  });

  const queryParams = useMemo(
    () => ({
      matterId: matterId || undefined,
      q: state.search || undefined,
      ordering: state.ordering || '-created_at',
      status: (state.filters.status as string) || undefined,
      limit: state.limit,
      offset: state.offset,
    }),
    [
      matterId,
      state.search,
      state.ordering,
      state.filters,
      state.limit,
      state.offset,
    ],
  );

  const { data, isLoading, error } = usePostgridMailablesListQuery(queryParams);

  const mailables = (data?.results || []) as MailableItem[];
  const totalCount = data?.count || 0;

  const columns = useMemo(
    () => createColumns(onDetailsClick),
    [onDetailsClick],
  );

  const handleRowClick = (item: MailableItem) => {
    onDetailsClick?.(item.id, item.mailable_type);
  };

  const handleFilterChange = (
    columnId: string,
    value: string | string[] | null,
  ) => {
    const newFilters = { ...state.filters };

    if (value === null || (Array.isArray(value) && value.length === 0)) {
      delete newFilters[columnId];
    } else {
      newFilters[columnId] = value;
    }

    actions.setFilters(newFilters);
  };

  const filterValues = useMemo(() => {
    const result: Record<string, string | string[] | null> = {};

    Object.entries(state.filters).forEach(([key, value]) => {
      if (typeof value === 'boolean' || typeof value === 'number') {
        result[key] = String(value);
      } else {
        result[key] = value;
      }
    });

    return result;
  }, [state.filters]);

  const hasActiveFilters =
    !!state.search || Object.keys(state.filters).length > 0;

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] py-16 px-4">
        <div className="p-3 rounded-full bg-red-50 mb-4">
          <XCircleIcon className="h-6 w-6 text-red-500" />
        </div>
        <h3 className="text-sm font-medium text-zinc-900 mb-1">
          Failed to load mail
        </h3>
        <p className="text-sm text-zinc-500">Please try refreshing the page.</p>
      </div>
    );
  }

  // Follow TaskListView pattern exactly for table wrapper
  return (
    <div
      className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900"
      style={{ contain: 'layout' }}
    >
      <DataTable
        data={mailables}
        columns={columns}
        getRowKey={(row) => row.id}
        getRowClassName={() => 'group'}
        // Search
        searchEnabled
        searchPlaceholder="Search recipients..."
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        disableClientFilter
        // Pagination
        paginationEnabled
        currentPage={actions.getCurrentPage()}
        totalPages={actions.getTotalPages(totalCount)}
        totalCount={totalCount}
        pageSize={state.limit}
        onPageChange={actions.setPage}
        onPageSizeChange={actions.setLimit}
        // Sorting - wrap setOrdering to toggle asc/desc instead of clearing
        onSortChange={(field, direction) => {
          if (direction) {
            // Direction provided (from filter dropdown) - use as-is
            actions.setOrdering(field, direction);
          } else {
            // No direction (from column header click) - toggle between asc/desc
            const currentField = state.ordering?.replace('-', '') || '';
            const isDesc = state.ordering?.startsWith('-');
            if (currentField === field) {
              // Same field - toggle direction
              actions.setOrdering(field, isDesc ? 'asc' : 'desc');
            } else {
              // New field - default to descending for dates
              actions.setOrdering(field, 'desc');
            }
          }
        }}
        sortField={
          state.ordering ? state.ordering.replace('-', '') : 'created_at'
        }
        sortDirection={state.ordering?.startsWith('-') ? 'desc' : 'asc'}
        // Filtering
        onFilterChange={handleFilterChange}
        filterValues={filterValues}
        // States
        isLoading={isLoading}
        emptyStateConfig={getEmptyStateConfig(matterId, hasActiveFilters)}
        // Interaction
        onRowClick={handleRowClick}
        density="compact"
      />
    </div>
  );
};
